﻿import type { IServerSideGetRowsRequest, ProvidedFilterModel } from 'ag-grid-community';
import type { FilterTranslator } from 'components/shared/Blotter/Grid/Hooks/requestTranslator';
import { first } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

export function useTranslateFilters<FilterModel extends ProvidedFilterModel, TOutput = string | null, TData = unknown>(
  filterTranslators: readonly FilterTranslator<FilterModel, TOutput>[],
  columns?: readonly IBlotterColumnDefinition<TData>[],
) {
  const colNameToColumnType = useMemo(() => {
    const pair = (columns ?? [])
      .map(col => [col.field, col.filterOptions?.filterType])
      .filter(([colName, filterType]) => colName !== undefined && filterType !== undefined) as [string, string][];
    return Object.fromEntries(pair);
  }, [columns]);

  return useCallback(
    (request: IServerSideGetRowsRequest) => {
      const filters = Object.entries(request.filterModel ?? {}).reduce(
        (obj: Record<string, Nullable<TOutput>>, [colId, filterModel]: [string, FilterModel]) => {
          const columnType = colNameToColumnType[colId];

          const matchingTranslators = filterTranslators.filter(
            translator => translator.filterType === filterModel.filterType || translator.filterType === columnType,
          );
          const translator =
            matchingTranslators.find(translator => translator.col === colId) ?? first(matchingTranslators);
          if (translator) {
            const translated = translator?.translate(filterModel, colId);
            obj[colId] = translated;
          }
          return obj;
        },
        {},
      );
      return filters;
    },
    [filterTranslators, colNameToColumnType],
  );
}
